'use client';

import classnames from 'classnames';
import React, { useState } from 'react';

import { DROPDOWN_MENU_ITEM_CLASSNAME } from '@cineamo/ui/src/dropdown/dropdown-colors';
import DropdownPanel from '@cineamo/ui/src/dropdown/DropdownPanel';

import { Link } from '@/i18n/routing';

type NavigationItemMenuProps = {
    trigger: React.JSX.Element;
    href: string;
    items: Array<{
        text: string;
        /** An anchor on the menu's own page, or an absolute site path (`/about-us`) of its own. */
        href: string;
    }>;
};

const NavigationItemMenu: React.FC<NavigationItemMenuProps> = ({ trigger, href, items }) => {
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    return (
        <div
            className="relative"
            role="menu"
            tabIndex={0}
            onFocus={() => setIsPanelOpen(true)}
            onMouseEnter={() => setIsPanelOpen(true)}
            onMouseLeave={() => setIsPanelOpen(false)}>
            {trigger}
            {isPanelOpen && (
                /**
                 * The 10px gap between trigger and panel is this wrapper's transparent PADDING, not
                 * a margin on the panel and not a separate spacer element.
                 *
                 * `onMouseLeave` on the outer div closes the menu, and it only fires when the
                 * pointer leaves that div and all its descendants — so the gap has to be inside a
                 * descendant, or crossing it closes the menu the pointer is travelling towards.
                 * Two earlier attempts at that both failed: the spacer's classes were built by
                 * interpolating a variable into the string (`h-${menuMarginPx}`), which Tailwind's
                 * static extractor cannot see, so no rule was generated at all; and once written
                 * literally it was `w-full` — the TRIGGER's width — while the panel is `right-0`
                 * and, with `whitespace-nowrap` rows, usually wider. That bridged the gap only
                 * directly beneath the trigger, so a diagonal move toward the rest of the panel
                 * still crossed dead space.
                 *
                 * Padding on the wrapper cannot get that wrong: the wrapper shrink-wraps the panel,
                 * so the bridge is exactly as wide as the panel it leads to.
                 */
                <div className="absolute top-full right-0 z-[60] pt-10">
                    <DropdownPanel className="flex flex-col rounded-8">
                        {items.map((item) => (
                            <Link
                                onClick={() => setIsPanelOpen(false)}
                                key={item.href}
                                // Same switch Footer.tsx uses: an absolute path is a page in its own
                                // right and keeps it, anything else is an anchor on the menu's page.
                                href={item.href.startsWith('/') ? item.href : `${href}#${item.href}`}
                                className={classnames(
                                    'py-8 px-16 text-body-14 whitespace-nowrap',
                                    DROPDOWN_MENU_ITEM_CLASSNAME
                                )}>
                                {item.text}
                            </Link>
                        ))}
                    </DropdownPanel>
                </div>
            )}
        </div>
    );
};

export default NavigationItemMenu;
